import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { RootState } from '@store/index'
import { extractApiError } from '@utils/apiError.util'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  createEmbeddedOnboardingSession,
  createStripeLoginLink,
  createStripeAccount,
  getStripeAccountStatus,
  type StripeAccountStatus,
} from '@services/api/billing/stripe-account.api'

type UseStripeIntegrationHooks = {
  clinicId: string | null
  accountStatus: StripeAccountStatus | null
  hasMapping: boolean
  isLoading: boolean
  dataReady: boolean
  error: string | null
  isProvisioning: boolean
  isLaunchingEmbedded: boolean
  fetchStatus: () => Promise<void>
  createOrConnectStripeAccount: () => Promise<boolean>
  startEmbeddedOnboarding: () => Promise<string | null>
  openStripeDashboard: () => Promise<void>
}

type UseStripeIntegrationOptions = {
  loadingContainer?: string
}

const DEFAULT_LOADING_CONTAINER = '.settings-integrations'

export const useStripeIntegration = (
  options: UseStripeIntegrationOptions = {},
): UseStripeIntegrationHooks => {
  const { loadingContainer = DEFAULT_LOADING_CONTAINER } = options
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const selectedClinic = useSelector(
    (state: RootState) => state.clinic.selectedClinic,
  )
  const clinicId = selectedClinic?.clinic_id ?? null
  const [accountStatus, setAccountStatus] =
    useState<StripeAccountStatus | null>(null)
  const [hasMapping, setHasMapping] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [dataReady, setDataReady] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [isProvisioning, setIsProvisioning] = useState<boolean>(false)
  const [isLaunchingEmbedded, setIsLaunchingEmbedded] = useState<boolean>(false)

  const fetchStatus = useCallback(async (): Promise<void> => {
    if (!clinicId) {
      setAccountStatus(null)
      setHasMapping(false)
      setError('No clinic selected.')
      setIsLoading(false)
      setDataReady(true)
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const response = await getStripeAccountStatus(clinicId)
      if (response.statusCode !== 200 || !response.data?.account_status) {
        throw new Error(response.message || 'Failed to fetch Stripe status.')
      }
      setAccountStatus(response.data.account_status)
      setHasMapping(true)
    } catch (err: unknown) {
      const message = extractApiError(err, 'Failed to fetch Stripe status.')
      const normalized = message.toLowerCase()
      if (
        normalized.includes('no stripe account') ||
        normalized.includes('not found')
      ) {
        setHasMapping(false)
        setAccountStatus(null)
        setError(null)
      } else {
        setError(message)
      }
    } finally {
      setIsLoading(false)
      setDataReady(true)
    }
  }, [clinicId])

  const createOrConnectStripeAccount =
    useCallback(async (): Promise<boolean> => {
      if (!clinicId) return false
      setIsProvisioning(true)
      showLoading({ container: loadingContainer })
      try {
        const response = await createStripeAccount(clinicId)
        if (response.statusCode < 200 || response.statusCode >= 300) {
          throw new Error(
            response.message || 'Failed to create Stripe account mapping.',
          )
        }
        showToast(
          createSuccessToast(
            'Stripe account created',
            'Stripe account mapping has been provisioned for this clinic.',
          ),
        )
        await fetchStatus()
        return true
      } catch (err: unknown) {
        const message = extractApiError(
          err,
          'Failed to create Stripe account mapping.',
        )
        showToast(createFailedToast('Stripe account', message))
        return false
      } finally {
        setIsProvisioning(false)
        hideLoading(loadingContainer)
      }
    }, [
      clinicId,
      fetchStatus,
      hideLoading,
      loadingContainer,
      showLoading,
      showToast,
    ])

  const startEmbeddedOnboarding = useCallback(async (): Promise<
    string | null
  > => {
    if (!clinicId) return null
    setIsLaunchingEmbedded(true)
    try {
      const response = await createEmbeddedOnboardingSession(clinicId)
      if (
        response.statusCode < 200 ||
        response.statusCode >= 300 ||
        !response.data?.client_secret
      ) {
        throw new Error(
          response.message || 'Failed to start embedded onboarding.',
        )
      }
      return response.data.client_secret
    } catch (err: unknown) {
      const message = extractApiError(
        err,
        'Failed to start embedded onboarding.',
      )
      showToast(createFailedToast('Embedded onboarding', message))
      return null
    } finally {
      setIsLaunchingEmbedded(false)
    }
  }, [clinicId, showToast])

  const openStripeDashboard = useCallback(async (): Promise<void> => {
    if (!clinicId) return
    showLoading({ container: loadingContainer })
    try {
      const response = await createStripeLoginLink(clinicId)
      if (
        response.statusCode < 200 ||
        response.statusCode >= 300 ||
        !response.data?.url
      ) {
        throw new Error(
          response.message || 'Failed to create Stripe login link.',
        )
      }
      window.open(response.data.url, '_blank', 'noopener,noreferrer')
    } catch (err: unknown) {
      const message = extractApiError(err, 'Failed to open Stripe dashboard.')
      showToast(createFailedToast('Stripe dashboard', message))
    } finally {
      hideLoading(loadingContainer)
    }
  }, [clinicId, hideLoading, loadingContainer, showLoading, showToast])

  useEffect(() => {
    setDataReady(false)
    void fetchStatus()
  }, [fetchStatus])

  return useMemo(
    () => ({
      clinicId,
      accountStatus,
      hasMapping,
      isLoading,
      dataReady,
      error,
      isProvisioning,
      isLaunchingEmbedded,
      fetchStatus,
      createOrConnectStripeAccount,
      startEmbeddedOnboarding,
      openStripeDashboard,
    }),
    [
      clinicId,
      accountStatus,
      hasMapping,
      isLoading,
      dataReady,
      error,
      isProvisioning,
      isLaunchingEmbedded,
      fetchStatus,
      createOrConnectStripeAccount,
      startEmbeddedOnboarding,
      openStripeDashboard,
    ],
  )
}
